import { NativeModules, Platform } from 'react-native';
import { requestCameraPermissionsAsync } from 'expo-image-picker';
// The legacy entry point: the root 'expo-file-system' export of getInfoAsync
// throws on SDK 54+, so every scan was recorded with size 0.
import * as FileSystem from 'expo-file-system/legacy';
import { generateWithApple, isAppleIntelligenceAvailable } from './appleIntelligence';
import { recorder } from '../services/sessionTelemetry/recorder';

export interface ScannedFile {
  uri: string;
  name: string;
  mimeType: string;
  size: number;
}

/**
 * Returns true if the device supports native document scanning.
 * Guards against simulator builds and other environments where the native
 * module is not registered.
 *
 * Web/desktop have no equivalent — the option is hidden on those platforms
 * (the file chooser sheet uses this to decide whether to show the row at
 * all, which is what the Files screen / chat upload sheet / proposed-action
 * upload sheet all share). The previous `Platform.OS !== 'ios'` short
 * circuit returned true on web too, so the row appeared but `scanDocument`
 * had no native module to call and just no-op'd.
 */
export function isScannerAvailable(): boolean {
  if (Platform.OS === 'web') return false;
  if (Platform.OS !== 'ios') return true;
  if (!NativeModules.RNDocumentScanner && !NativeModules.DocumentScanner) return false;
  return true;
}

/**
 * Wraps DocumentScanner.scanDocument() with a timeout so the promise
 * doesn't hang silently if the native VC presentation fails (e.g. nil
 * topViewController during a modal dismissal transition).
 *
 * The deadline runs from when the scanner opens to when the person taps Save,
 * so it has to outlast a real scan. It was 60 seconds: a multi-page scan took
 * longer, the promise rejected while the scanner still covered the screen (so
 * the "Scan Failed" alert went unseen), and the pages saved afterwards were
 * dropped without a single network call (2026-10-21, a user's long scan).
 */
const SCAN_TIMEOUT_MS = 30 * 60 * 1000;

function scanWithTimeout(
  DocumentScanner: any,
  options: object,
  timeoutMs = SCAN_TIMEOUT_MS,
): Promise<{ scannedImages?: string[]; ocrText?: string; status?: string }> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Document scanner timed out — please try again.'));
    }, timeoutMs);

    DocumentScanner.scanDocument(options)
      .then((result: any) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((err: any) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

/**
 * Derive a descriptive base filename (no extension) from OCR text.
 * Uses Apple Intelligence when available; falls back to a plain date string.
 */
async function deriveFilename(ocrText: string, pageCount: number): Promise<string> {
  const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fallback = pageCount === 1 ? `Scanned Document - ${date}` : `Scanned Documents - ${date}`;

  if (!ocrText || !isAppleIntelligenceAvailable()) return fallback;

  const snippet = ocrText.slice(0, 400);
  const generated = await generateWithApple(
    `Create a short, descriptive filename (4–6 words) for a scanned document based on this text excerpt. No extension, no quotes, no punctuation at the end.\n\n"${snippet}"`,
    24,
  );

  return generated || fallback;
}

export async function scanDocument(): Promise<ScannedFile[]> {
  if (!isScannerAvailable()) {
    throw new Error('Document scanning is not available on this device.');
  }

  // Explicitly request camera permission before launching the scanner.
  // VNDocumentCameraViewController will silently fail on iOS if permission
  // has been denied without showing any system alert.
  if (Platform.OS === 'ios') {
    const { status } = await requestCameraPermissionsAsync();
    if (status !== 'granted') {
      throw new Error(
        'Camera access is required to scan documents. Please enable it in Settings > Eleplan > Camera.',
      );
    }
  }

  // Dynamic require so the module is only touched when we know it's available.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const DocumentScanner = require('react-native-document-scanner-plugin').default;

  // Scans are counted (no content) so a scan that never became an upload can
  // be told apart from one that never happened.
  const startedAt = Date.now();
  recorder.track('document_scan_started');
  let result: { scannedImages?: string[]; ocrText?: string; status?: string };
  try {
    // No maxNumDocuments: it is Android's page cap (ML Kit setPageLimit; iOS
    // ignores it), and a records scan can run well past 20 pages. Unset, the
    // scanner has no page limit.
    result = await scanWithTimeout(DocumentScanner, {
      croppedImageQuality: 100,
      letUserAdjustCrop: true,
    });
  } catch (err: any) {
    recorder.track('document_scan_failed', {
      seconds: Math.round((Date.now() - startedAt) / 1000),
      reason: /timed out/i.test(String(err?.message)) ? 'timeout' : 'error',
    });
    throw err;
  }

  // User cancelled — return empty without error
  if (result?.status === 'cancel' || !result?.scannedImages?.length) {
    recorder.track('document_scan_cancelled', { seconds: Math.round((Date.now() - startedAt) / 1000) });
    return [];
  }
  recorder.track('document_scan_completed', {
    pages: result.scannedImages.length,
    seconds: Math.round((Date.now() - startedAt) / 1000),
  });

  const images = result.scannedImages!;
  const ocrText = result.ocrText || '';

  // Generate a descriptive filename from the OCR text (async, may use Apple Intelligence)
  const basename = await deriveFilename(ocrText, images.length);

  // Resolve file sizes in parallel — needed for document record and display
  const sizes = await Promise.all(
    images.map(async (uri: string) => {
      try {
        const info = await FileSystem.getInfoAsync(uri);
        return (info.exists && 'size' in info) ? (info as any).size ?? 0 : 0;
      } catch {
        return 0;
      }
    }),
  );

  return images.map((uri: string, index: number) => ({
    uri,
    name: images.length === 1 ? `${basename}.jpg` : `${basename} - Page ${index + 1}.jpg`,
    mimeType: 'image/jpeg',
    size: sizes[index] ?? 0,
  }));
}
